import { Table, Input, Card, Tag, Typography, Space, Tooltip } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { useTranslation } from "react-i18next";
import { useState, useMemo } from "react";
import { 
  SearchOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  HourglassOutlined,
  UserOutlined
} from "@ant-design/icons";
import { useGetLevelCCandidates } from "@hooks";
import { LevelCReportFilter, LevelCCandidateSummary } from "../../types/levelc-reports";
import dayjs from "dayjs";

interface LevelCCandidateTableProps {
  filter: LevelCReportFilter;
  onCandidateClick?: (candidate: LevelCCandidateSummary) => void;
}

export const LevelCCandidateTable = ({
  filter,
  onCandidateClick,
}: LevelCCandidateTableProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("examDate");
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useGetLevelCCandidates({
    filter,
    search: search || undefined,
    sortBy,
    sortOrder,
    page,
    pageSize,
  });

  const columns = useMemo<ColumnsType<LevelCCandidateSummary>>(() => [
    {
      title: t("levelCReports.candidates.name"),
      dataIndex: "candidateName",
      key: "candidateName",
      sorter: true,
      render: (name: string) => (
        <Space>
          <UserOutlined style={{ color: "#1890ff" }} />
          <Typography.Text strong>{name}</Typography.Text>
        </Space>
      ),
    },
    {
      title: t("levelCReports.candidates.examDate"),
      dataIndex: "examDate",
      key: "examDate",
      sorter: true,
      defaultSortOrder: "descend",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
      width: 120,
    },
    {
      title: t("levelCReports.candidates.term"),
      dataIndex: "termName",
      key: "termName",
      ellipsis: true,
      width: 200,
    },
    {
      title: t("levelCReports.candidates.competencies"),
      key: "competencies",
      sorter: true,
      render: (_: any, record: LevelCCandidateSummary) => {
        const passRate = record.competenciesTotal > 0 
          ? (record.competenciesPassed / record.competenciesTotal) * 100 
          : 0;
        const color = passRate < 50 ? "#ff4d4f" : passRate <= 70 ? "#faad14" : "#52c41a";
        return (
          <Tooltip title={`${passRate.toFixed(0)}% ${t("levelCReports.candidates.passed")}`}>
            <Typography.Text style={{ color }}>
              {record.competenciesPassed} / {record.competenciesTotal}
            </Typography.Text>
          </Tooltip>
        );
      },
      width: 120,
    },
    {
      title: t("levelCReports.candidates.score"),
      key: "totalScore",
      sorter: true,
      render: (_: any, record: LevelCCandidateSummary) => (
        <Typography.Text>
          {record.totalScore.toFixed(1)} / {record.maxScore.toFixed(1)}
        </Typography.Text>
      ),
      width: 120,
    },
    {
      title: t("levelCReports.candidates.status"),
      key: "hasPassed",
      sorter: true,
      render: (_: any, record: LevelCCandidateSummary) => {
        // Grading status 2 = Complete
        if (record.gradingStatus !== 2) {
          return (
            <Tag icon={<HourglassOutlined />} color="processing">
              {t("levelCReports.candidates.statusPending")}
            </Tag>
          );
        }
        return record.hasPassed ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("levelCReports.candidates.statusPassed")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error">
            {t("levelCReports.candidates.statusFailed")}
          </Tag>
        );
      },
      width: 120,
    },
    {
      title: t("levelCReports.candidates.grading"),
      dataIndex: "gradingStatusText",
      key: "gradingStatus",
      sorter: true,
      render: (status: string, record: LevelCCandidateSummary) => {
        const colors: Record<number, string> = {
          0: "default",
          1: "processing",
          2: "success",
        };
        return <Tag color={colors[record.gradingStatus] || "default"}>{status}</Tag>;
      },
      width: 130,
    },
  ], [t]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<LevelCCandidateSummary> | SorterResult<LevelCCandidateSummary>[]
  ) => {
    // Handle pagination
    if (pagination.current && pagination.pageSize) {
      setPage(pagination.current);
      setPageSize(pagination.pageSize);
    }

    // Handle sorting
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    if (singleSorter.columnKey) {
      const fieldMap: Record<string, string> = {
        candidateName: "candidateName",
        examDate: "examDate",
        competencies: "competenciesPassed",
        totalScore: "totalScore",
        hasPassed: "hasPassed",
        gradingStatus: "gradingStatus",
      };
      setSortBy(fieldMap[singleSorter.columnKey as string] || "examDate");
      setSortOrder(singleSorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1); // Reset to first page on search
  };

  return (
    <Card 
      title={
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t("levelCReports.candidates.title")}
        </Typography.Title>
      }
      extra={
        <Input.Search
          placeholder={t("levelCReports.candidates.searchPlaceholder")}
          allowClear
          onSearch={handleSearch}
          onChange={(e) => !e.target.value && handleSearch("")}
          style={{ width: 250 }}
          prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        />
      }
      style={{ marginBottom: 24 }}
    >
      <Table
        columns={columns}
        dataSource={data?.candidates}
        rowKey="examInstanceId"
        loading={isLoading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data?.totalCount || 0,
          showSizeChanger: true,
          showTotal: (total, range) =>
            t("levelCReports.candidates.pagination", {
              start: range[0],
              end: range[1],
              total,
            }),
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
        onRow={(record) => ({
          onClick: () => onCandidateClick?.(record),
          style: { cursor: onCandidateClick ? "pointer" : "default" },
        })}
        scroll={{ x: 900 }}
        size="middle"
      />
    </Card>
  );
};

